import { useState } from 'react'
import { Resource } from '@/shared/types'
import { Hash, Bot, Zap, ChevronRight, Loader2 } from 'lucide-react'

interface ResourceCardProps {
  resource: Resource
  onClick: () => void
}

export default function ResourceCard({ resource, onClick }: ResourceCardProps) {
  const isActive = resource.status === 'active'
  const [imageLoading, setImageLoading] = useState(true)
  const [imageError, setImageError] = useState(false)

  const getLogoUrl = () => {
    if (!resource.id) return null

    const baseUrl = import.meta.env.VITE_OPSERVICE_BASE_URL || 'http://localhost:8080'
    return `${baseUrl}/api/v1/resources/${resource.id}/logo`
  }

  const logoUrl = getLogoUrl()

  const getResourceIcon = () => {
    switch (resource.resource_type) {
      case 'bot':
        return <Bot className="w-5 h-5" />
      case 'boost':
        return <Zap className="w-5 h-5" />
      case 'channel':
      case 'group':
      default:
        return <Hash className="w-5 h-5" />
    }
  }

  const getIconBackground = () => {
    if (isActive) {
      return 'bg-tg-button shadow-lg shadow-tg-button/25'
    }
    return 'bg-tg-bg'
  }

  const getIconColor = () => {
    return isActive ? 'text-tg-button-text' : 'text-[#6d7885]'
  }

  return (
    <div
      onClick={onClick}
      className="group relative bg-tg-secondary-bg rounded-2xl p-4 shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-200 cursor-pointer overflow-hidden"
      style={{
        boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.15)'
      }}
    >
      {/* Gradient overlay for active resources */}
      {isActive && (
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/5 to-transparent rounded-full blur-2xl" />
      )}

      <div className="relative flex items-center gap-3">
        {/* Resource Logo or Icon */}
        <div className={`relative flex-shrink-0 w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center ${getIconBackground()}`}>
          {logoUrl && !imageError ? (
            <>
              <img
                src={logoUrl}
                alt={`${resource.name} logo`}
                className={`w-full h-full object-cover transition-opacity duration-200 ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
                onError={() => {
                  setImageError(true)
                  setImageLoading(false)
                }}
                onLoad={() => {
                  setImageLoading(false)
                }}
              />
              {imageLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className={`w-5 h-5 animate-spin ${getIconColor()}`} />
                </div>
              )}
            </>
          ) : (
            <div className={getIconColor()}>
              {getResourceIcon()}
            </div>
          )}
        </div>

        {/* Resource Info */}
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-tg-text truncate mb-0.5">
            {resource.name}
          </h3>
          <p className="text-sm text-tg-hint truncate">
            {resource.telegram_id || resource.username || 'ID не указан'}
          </p>
        </div>

        {/* Arrow */}
        <ChevronRight className="w-5 h-5 text-tg-hint/50 group-hover:text-tg-hint group-hover:translate-x-1 transition-all flex-shrink-0" />
      </div>

      {/* Status and price badges */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {/* Status badge */}
        {(() => {
          const statusStyles = {
            active: {
              label: 'Активен',
              bgColor: 'bg-green-500/10',
              textColor: 'text-green-600',
              borderColor: 'border-green-500/30',
              dotColor: 'bg-green-500',
            },
            inactive: {
              label: 'Неактивен',
              bgColor: 'bg-gray-500/10',
              textColor: 'text-gray-600',
              borderColor: 'border-gray-500/30',
              dotColor: 'bg-gray-500',
            },
            pending: {
              label: 'Ожидает проверки',
              bgColor: 'bg-yellow-500/10',
              textColor: 'text-yellow-600',
              borderColor: 'border-yellow-500/30',
              dotColor: 'bg-yellow-500',
            },
            rejected: {
              label: 'Отклонен',
              bgColor: 'bg-red-500/10',
              textColor: 'text-red-500',
              borderColor: 'border-red-500/30',
              dotColor: 'bg-red-500',
            },
            maintenance: {
              label: 'На обслуживании',
              bgColor: 'bg-orange-500/10',
              textColor: 'text-orange-600',
              borderColor: 'border-orange-500/30',
              dotColor: 'bg-orange-500',
            },
          }

          // For pending status: only show "Ожидает проверки" if auto_resource is true
          // For non-auto-resources, treat pending as inactive (no moderation needed)
          let effectiveStatus = resource.status
          if (resource.status === 'pending' && !resource.auto_resource) {
            effectiveStatus = 'inactive'
          }

          const style = statusStyles[effectiveStatus as keyof typeof statusStyles] || statusStyles.inactive

          return (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap ${style.bgColor} ${style.textColor} border ${style.borderColor}`}>
              <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${style.dotColor}`} />
              {style.label}
            </span>
          )
        })()}

        {/* Price badge - only show if auto_resource is true */}
        {resource.auto_resource && (resource.final_reward || resource.base_reward) && (
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap border"
            style={{
              backgroundColor: 'rgba(82, 136, 193, 0.1)',
              color: '#5288c1',
              borderColor: 'rgba(82, 136, 193, 0.3)'
            }}
          >
            <Zap className="w-3 h-3" />
            {resource.final_reward || resource.base_reward} ₽
          </span>
        )}
      </div>
    </div>
  )
}
